"use client";

import * as React from "react";
import ChevronRight from "@/components/ui/chevron-right";
import ChevronLeft from "@/components/ui/chevron-left";
import type { Swiper as SwiperClass } from "swiper/types";
import { A11y, EffectCards, EffectCoverflow, EffectCreative, EffectCube, EffectFade, EffectFlip, Keyboard } from "swiper/modules";
import {
  Swiper as SwiperRoot,
  SwiperSlide,
  type SwiperProps,
} from "swiper/react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

type AppSwiperProps = SwiperProps & {
  className?: string;
  swiperClassName?: string;
  showNavigation?: boolean;
  navigationButtonClassName?: string;
  prevButtonClassName?: string;
  nextButtonClassName?: string;
};

function AppSwiper({
  className,
  swiperClassName,
  showNavigation = true,
  navigationButtonClassName,
  prevButtonClassName = "left-3",
  nextButtonClassName = "right-3",
  modules: modulesProp,
  onSwiper,
  onSlideChange,
  onResize,
  ...props
}: AppSwiperProps) {
  const [swiper, setSwiper] = React.useState<SwiperClass | null>(null);
  const [isBeginning, setIsBeginning] = React.useState(true);
  const [isEnd, setIsEnd] = React.useState(false);
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  const shouldRestartAutoplayRef = React.useRef(false);

  const effectKey = typeof props.effect === "string" ? props.effect : undefined;

  const modules = React.useMemo(() => {
    const effectModule =
      effectKey === "fade"
        ? EffectFade
        : effectKey === "creative"
          ? EffectCreative
          : effectKey === "cube"
            ? EffectCube
            : effectKey === "coverflow"
              ? EffectCoverflow
              : effectKey === "flip"
                ? EffectFlip
                : effectKey === "cards"
                  ? EffectCards
                  : null;

    const merged = [A11y, Keyboard, ...(effectModule ? [effectModule] : []), ...(modulesProp ?? [])];
    const unique: typeof merged = [];
    for (const swiperModule of merged) {
      if (!unique.includes(swiperModule)) unique.push(swiperModule);
    }
    return unique;
  }, [effectKey, modulesProp]);

  const updateEdgeState = React.useCallback((instance: SwiperClass) => {
    setIsBeginning(instance.isBeginning);
    setIsEnd(instance.isEnd);
  }, []);

  React.useEffect(() => {
    if (!swiper) return;

    const handleTransitionStart = () => setIsTransitioning(true);
    const handleTransitionEnd = () => {
      setIsTransitioning(false);
      if (!shouldRestartAutoplayRef.current) return;
      shouldRestartAutoplayRef.current = false;
      const autoplay = (swiper as unknown as { autoplay?: { start?: () => void } }).autoplay;
      autoplay?.start?.();
    };

    swiper.on("transitionStart", handleTransitionStart);
    swiper.on("transitionEnd", handleTransitionEnd);

    return () => {
      swiper.off("transitionStart", handleTransitionStart);
      swiper.off("transitionEnd", handleTransitionEnd);
    };
  }, [swiper]);

  const handleManualNavigation = React.useCallback(
    (direction: "prev" | "next") => {
      if (!swiper) return;
      if (isTransitioning) return;

      const autoplay = (swiper as unknown as { autoplay?: { stop?: () => void; start?: () => void } }).autoplay;
      if (autoplay?.stop && autoplay?.start) {
        autoplay.stop();
        shouldRestartAutoplayRef.current = true;
      }

      if (direction === "prev") swiper.slidePrev();
      else swiper.slideNext();
    },
    [isTransitioning, swiper],
  );

  const handleSwiper = React.useCallback(
    (instance: SwiperClass) => {
      setSwiper(instance);
      updateEdgeState(instance);
      onSwiper?.(instance);
    },
    [onSwiper, updateEdgeState],
  );

  const mergedProps: SwiperProps = { ...props };
  if (effectKey === "fade" && mergedProps.fadeEffect === undefined) {
    mergedProps.fadeEffect = { crossFade: true };
  }

  if (effectKey === "creative" && mergedProps.creativeEffect === undefined) {
    mergedProps.creativeEffect = {
      prev: { shadow: false, opacity: 0, translate: [0, 0, -200] },
      next: { shadow: false, opacity: 0, translate: ["100%", 0, 0] },
    };
  }

  if (effectKey === "cube" && mergedProps.cubeEffect === undefined) {
    mergedProps.cubeEffect = { shadow: false, slideShadows: false, shadowOffset: 20, shadowScale: 0.94 };
  }

  if (effectKey === "coverflow" && mergedProps.coverflowEffect === undefined) {
    mergedProps.coverflowEffect = { rotate: 0, stretch: 0, depth: 120, modifier: 1, slideShadows: false };
  }

  if (effectKey === "flip" && mergedProps.flipEffect === undefined) {
    mergedProps.flipEffect = { slideShadows: false, limitRotation: true };
  }

  if (effectKey === "cards" && mergedProps.cardsEffect === undefined) {
    mergedProps.cardsEffect = { slideShadows: false };
  }

  return (
    <div className={cn("relative", className)}>
      {showNavigation ? (
        <>
          <Button
            type="button"
            size="icon"
            className={cn(
              "absolute top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 rounded-full shadow cursor-pointer disabled:cursor-not-allowed sm:inline-flex",
              navigationButtonClassName,
              prevButtonClassName,
            )}
            onClick={() => handleManualNavigation("prev")}
            disabled={!swiper || isBeginning || isTransitioning}
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>

          <Button
            type="button"
            size="icon"
            className={cn(
              "absolute top-1/2 z-10 hidden h-9 w-9 -translate-y-1/2 rounded-full shadow cursor-pointer disabled:cursor-not-allowed sm:inline-flex",
              navigationButtonClassName,
              nextButtonClassName,
            )}
            onClick={() => handleManualNavigation("next")}
            disabled={!swiper || isEnd || isTransitioning}
            aria-label="Next slide"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </>
      ) : null}

      <SwiperRoot
        modules={modules}
        a11y={{ enabled: true }}
        keyboard={{ enabled: true }}
        grabCursor
        onSwiper={handleSwiper}
        onSlideChange={(instance) => {
          updateEdgeState(instance);
          onSlideChange?.(instance);
        }}
        onResize={(instance) => {
          updateEdgeState(instance);
          onResize?.(instance);
        }}
        className={cn("px-1 sm:px-12 lg:px-16", swiperClassName)}
        {...mergedProps}
      />
    </div>
  );
}

export { AppSwiper, SwiperSlide };
